<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-journal-medical fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">Consultations</h2>
          <small class="text-muted">
            {{ total.toLocaleString('fr-FR') }} consultation(s)
            <span v-if="medecinChoisi"> pour {{ medecinChoisi }}</span>
            <span v-if="du || au"> — du {{ dateLisible(du) }} au {{ dateLisible(au) }}</span>
          </small>
        </div>
      </div>
    </div>

    <resultats-a-voir />

    <div class="card p-3 mb-3 shadow-sm rounded card-clinique">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-4">
          <label class="form-label fw-semibold small mb-1">Rechercher</label>
          <input v-model="recherche" type="search" class="form-control form-control-clinique" placeholder="N° de consultation, motif, patient, téléphone, dossier…">
        </div>
        <div class="col-6 col-md-3">
          <label class="form-label fw-semibold small mb-1">Médecin</label>
          <select v-model="medecin" class="form-select form-select-clinique">
            <option value="">Tous les médecins</option>
            <option v-for="m in medecins" :key="m.id" :value="m.id">{{ m.nom }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Statut</label>
          <select v-model="statut" class="form-select form-select-clinique">
            <option value="">Tous</option>
            <option v-for="s in statuts" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div class="col-12 col-md-3">
          <label class="form-label fw-semibold small mb-1">Trier par</label>
          <div class="d-flex gap-1">
            <select v-model="tri" class="form-select form-select-clinique">
              <option value="co.createdAt">Date d'enregistrement</option>
              <option value="co.dateConsultation">Date de consultation</option>
              <option value="pat.nom">Nom du patient</option>
              <option value="co.statut">Statut</option>
            </select>
            <select v-model="sens" class="form-select form-select-clinique w-auto" aria-label="Ordre">
              <option value="desc">↓</option>
              <option value="asc">↑</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row g-2 align-items-end mt-1">
        <div class="col-12 col-md-4">
          <label class="form-label fw-semibold small mb-1">Période</label>
          <div class="btn-group w-100" role="group">
            <button v-for="p in periodes" :key="p.cle" type="button" class="btn btn-sm" :class="periodeRapide === p.cle ? 'btn-primary' : 'btn-outline-primary'" @click="choisirPeriode(p.cle)">{{ p.libelle }}</button>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Du</label>
          <input v-model="du" type="date" class="form-control form-control-clinique" @input="periodeRapide = ''">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Au</label>
          <input v-model="au" type="date" class="form-control form-control-clinique" @input="periodeRapide = ''">
        </div>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>

    <div class="card shadow-sm rounded card-clinique">
      <div class="table-responsive" :class="{ 'opacity-50': chargement }">
        <table class="table align-middle text-center mb-0 table-hover modern-table-clinique w-100">
          <thead>
            <tr>
              <th>#</th>
              <th>N° consultation</th>
              <th>Date</th>
              <th>Patient</th>
              <th class="d-none d-sm-table-cell">Téléphone</th>
              <th class="d-none d-md-table-cell">Type</th>
              <th class="d-none d-md-table-cell">Médecin</th>
              <th class="d-none d-md-table-cell">Paiement</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(c, i) in lignes" :key="c.id">
              <td class="text-muted">{{ rang(i) }}</td>
              <td>{{ c.numConsultation || '—' }}</td>
              <td>{{ formatDate(c.dateConsultation || c.createdAt) }}</td>
              <td class="text-start">
                <span class="fw-semibold">{{ c.patient_prenom }} {{ c.patient_nom }}</span>
                <div v-if="c.patient_numeroDossier" class="small text-muted">{{ c.patient_numeroDossier }}</div>
              </td>
              <td class="d-none d-sm-table-cell">{{ c.patient_telephone || '—' }}</td>
              <td class="d-none d-md-table-cell">{{ c.type_nom || '—' }}</td>
              <td class="d-none d-md-table-cell">
                <span v-if="c.medecin_nom">Dr {{ c.medecin_prenom }} {{ c.medecin_nom }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="d-none d-md-table-cell">
                <span :class="['badge', ['Payé', 'Pris en charge'].includes(c.estPaye) ? 'bg-success' : 'bg-warning text-dark']">{{ c.estPaye || '—' }}</span>
              </td>
              <td><span :class="['badge', classeStatut(c.statut)]">{{ c.statut || '—' }}</span></td>
              <td>
                <div v-if="$store.getters.hasAnyPermission(['PATIENT_DOSSIER', 'CONSULT_TRAITER', 'CONSULT_PAIEMENT'])" class="dropdown">
                  <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" v-dropdown-fixed>Actions</button>
                  <ul class="dropdown-menu">
                    <li v-if="c.estPaye === 'Non payé' && !estFinie(c) && $store.getters.hasPermission('CONSULT_PAIEMENT')">
                      <a class="dropdown-item text-success" href="#" @click.prevent="$router.push({ name: 'consultations_patient', params: { id: c.patient_id }, query: { payer: c.id } })"><i class="bi bi-cash-coin me-1"></i>Encaisser</a>
                    </li>
                    <li v-if="$store.getters.hasPermission('PATIENT_DOSSIER')"><a class="dropdown-item" href="#" @click.prevent="dossier(c.patient_id)">Dossier</a></li>
                    <li v-if="$store.getters.hasAnyPermission(['CONSULT_TRAITER', 'CONSULT_VIEW'])">
                      <a v-if="estFinie(c)" class="dropdown-item" href="#" @click.prevent="ouvrirDossier(c)">Voir la consultation</a>
                      <span v-else-if="paiementRequis(c) || !$store.getters.hasPermission('CONSULT_TRAITER')" class="dropdown-item disabled text-muted" title="La consultation doit d'abord être payée à la caisse">Traitement <small v-if="paiementRequis(c)">(à payer d'abord)</small></span>
                      <a v-else class="dropdown-item" href="#" @click.prevent="ouvrirDossier(c)">Traitement</a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr v-if="!chargement && !lignes.length">
              <td colspan="10" class="text-center text-muted py-4">{{ messageVide }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
      </div>
    </div>
  </div>
</template>

<script>
import api, { notifier } from '@/api';
import dayjs from 'dayjs';
import Pagination from '@/components/commun/Pagination.vue';
import { listeServeur } from '@/components/commun/listeServeur';
import ResultatsAVoir from '@/components/consultation/ResultatsAVoir.vue';

const AUJOURDHUI = () => dayjs().format('YYYY-MM-DD');

export default {
  name: 'ListeConsultation',
  components: { Pagination, ResultatsAVoir },
  mixins: [listeServeur('consultations', { tri: 'co.createdAt', sens: 'desc', parPage: 50 })],
  data() {
    return {
      du: AUJOURDHUI(),
      au: AUJOURDHUI(),
      periodeRapide: 'today',
      medecin: '',
      statut: '',
      medecins: [],
      statuts: ['En attente', 'En cours', 'Terminée', 'Annulée'],
      periodes: [
        { cle: 'today', libelle: "Aujourd'hui" },
        { cle: 'week', libelle: 'Semaine' },
        { cle: 'month', libelle: 'Mois' },
        { cle: '', libelle: 'Tout' },
      ],
    };
  },
  computed: {
    medecinChoisi() {
      const m = this.medecins.find(x => x.id === this.medecin);
      return m ? m.nom : '';
    },
    messageVide() {
      if (this.recherche.trim() || this.medecin || this.statut) return 'Aucune consultation ne correspond à la recherche.';
      if (this.du || this.au) return 'Aucune consultation sur cette période.';
      return 'Aucune consultation enregistrée.';
    },
  },
  watch: {
    du() { this.rechargerListe(); },
    au() { this.rechargerListe(); },
    medecin() { this.rechargerListe(); },
    statut() { this.rechargerListe(); },
  },
  created() {
    this.chargerMedecins();
  },
  methods: {
    filtresListe() {
      return { du: this.du, au: this.au, medecin: this.medecin, statut: this.statut };
    },
    async chargerMedecins() {
      // Liste réservée aux rôles qui voient l'accueil (évite un refus inutile)
      if (!this.$store.getters.hasAnyPermission(['RDV_VOIR', 'RDV_GERER', 'SALLE_ATTENTE', 'CONSULT_VIEW', 'CONSULT_TRAITER'])) return;
      try {
        const tous = (await api.custom('rendez-vous/medecins', 'GET')).data?.data || [];
        const soignants = tous.filter(m => m.estMedecin);
        this.medecins = (soignants.length ? soignants : tous).map(m => ({ id: m.id, nom: `Dr ${m.nom}` }));
      } catch {
        this.medecins = [];
      }
    },
    choisirPeriode(cle) {
      const j = dayjs();
      this.periodeRapide = cle;
      if (cle === 'today') { this.du = j.format('YYYY-MM-DD'); this.au = this.du; }
      else if (cle === 'week') { this.du = j.startOf('week').format('YYYY-MM-DD'); this.au = j.endOf('week').format('YYYY-MM-DD'); }
      else if (cle === 'month') { this.du = j.startOf('month').format('YYYY-MM-DD'); this.au = j.endOf('month').format('YYYY-MM-DD'); }
      else { this.du = ''; this.au = ''; }
    },
    dateLisible(d) {
      return d ? dayjs(d).format('DD/MM/YYYY') : '…';
    },
    formatDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '—';
    },
    classeStatut(statut) {
      const s = String(statut || '');
      if (s.startsWith('Termin')) return 'bg-success';
      if (s.startsWith('Annul')) return 'bg-secondary';
      if (s === 'En cours') return 'bg-info text-dark';
      return 'bg-warning text-dark';
    },
    dossier(id) {
      this.$router.push({ name: 'information_personnelle', params: { id } });
    },
    creation() {
      this.$router.push({ name: 'create_consultation' });
    },
    /** Non payée et payante : pas de traitement, sauf droit d'urgence. */
    paiementRequis(c) {
      if (c.estPaye === 'Payé' || c.estPaye === 'Pris en charge' || this.$store.getters.hasPermission('CONSULT_SANS_PAIEMENT')) return false;
      const prix = c.type?.prix ?? c.type_prix;
      return !(prix !== undefined && prix !== null && Number(prix) <= 0);
    },
    estFinie(c) {
      return /^(termin|annul)/i.test(String(c.statut || ''));
    },
    /** En cours : poste de consultation ; terminée ou annulée : dossier en lecture. */
    ouvrirDossier(c) {
      if (!this.estFinie(c) && this.paiementRequis(c)) {
        notifier("Cette consultation n'est pas encore payée : le patient doit d'abord passer à la caisse.", 'danger');
        return;
      }
      this.$router.push({ name: 'traitement_consultation', params: { id: c.id } });
    },
  },
};
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.form-control-clinique, .form-select-clinique { border-radius: 10px; border: 1px solid #d1e3f0; }
.form-control-clinique:focus, .form-select-clinique:focus { box-shadow: 0 0 6px #0d6efd33; border-color: #0d6efd; }
.modern-table-clinique thead { background: linear-gradient(90deg, #8fd3f4, #0d6efd); color: #fff; }
.modern-table-clinique thead th { background: transparent; color: #fff; font-weight: 600; }
@media (max-width: 767px) {
  .table td, .table th { padding: 0.4rem 0.5rem; font-size: 0.8rem; }
}
</style>
